import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  IconButton,
  useTheme,
  useMediaQuery,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Badge,
  Tooltip,
} from '@mui/material';
import '../styles/mobile.css';
import MobileNavigation from './MobileNavigation';
import useMobile from '../hooks/useMobile';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Vaccines as VaccinesIcon,
  MedicalServices as MedicalServicesIcon,
  Calculate as CalculateIcon,
  Description as DescriptionIcon,
  PhotoCamera as PhotoCameraIcon,
  ChildCare as ChildCareIcon,
  Psychology as PsychologyIcon,
  LocalHospital as LocalHospitalIcon,
  Security as SecurityIcon,
  Person as PersonIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
  History as HistoryIcon,
  Star as StarIcon,
  Settings as SettingsIcon,
  PrivacyTip as PrivacyTipIcon,
  Search as SearchIcon,
  BugReport as BugReportIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AuthModal from './AuthModal';
import HistoryPanel from './HistoryPanel';
import FavoritesPanel from './FavoritesPanel';
import Breadcrumbs from './Breadcrumbs';
import SectionSearch from './SectionSearch';
import QuickLinks from './QuickLinks';
import { useNavigation } from '../hooks/useNavigation';

interface LayoutProps {
  children: React.ReactNode;
}

const drawerWidth = 280;

const menuItems = [
  { text: 'Главная', icon: <DashboardIcon />, path: '/' },
  { text: 'Вакцинация', icon: <VaccinesIcon />, path: '/vaccination' },
  { text: 'Неотложная помощь', icon: <LocalHospitalIcon />, path: '/emergency' },
  { text: 'Дети до года', icon: <ChildCareIcon />, path: '/children-up-to-year' },
  { text: 'Атлас заболеваний', icon: <PhotoCameraIcon />, path: '/atlas' },
  { text: 'Калькулятор', icon: <CalculateIcon />, path: '/calculator' },
  { text: 'ИИ-помощник', icon: <PsychologyIcon />, path: '/ai-assistant' },
  { text: 'Приказы', icon: <DescriptionIcon />, path: '/orders' },
  { text: 'Клинические рекомендации', icon: <AssignmentIcon />, path: '/clinical-recommendations' },
  { text: 'Системные настройки', icon: <SettingsIcon />, path: '/system-settings', hidden: true },
];

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [historyPanelOpen, setHistoryPanelOpen] = useState(false);
  const [favoritesPanelOpen, setFavoritesPanelOpen] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, isAuthenticated, logout } = useAuth();
  const { breadcrumbs, currentSection, navigateTo } = useNavigation();
  const { isMobile: isMobileDevice } = useMobile();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path: string) => {
    navigateTo(path);
    setMobileOpen(false);
  };

  const handleSearchClick = () => {
    setShowSearch(true);
  };

  const handleHistoryClick = () => {
    setHistoryPanelOpen(true);
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogin = () => {
    setAuthModalOpen(true);
    handleUserMenuClose();
  };

  const handleLogout = () => {
    logout();
    handleUserMenuClose();
  };

  const handleAuthSuccess = () => {
    setAuthModalOpen(false);
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 3, borderBottom: '1px solid #E9ECEF' }}>
        <Typography variant="h6" sx={{ fontWeight: 500, color: '#5B7C99' }}>
          Педиатрия
        </Typography>
        <Typography variant="body2" sx={{ color: '#6C757D', mt: 0.5 }}>
          Справочник для врачей
        </Typography>
      </Box>
      <List sx={{ flex: 1, pt: 2 }}>
        {menuItems.filter(item => !item.hidden).map((item) => (
          <ListItem
            key={item.text}
            onClick={() => handleNavigation(item.path)}
            sx={{
              mx: 1,
              mb: 0.5,
              borderRadius: 1.5,
              cursor: 'pointer',
              backgroundColor: location.pathname === item.path ? '#F0F4F8' : 'transparent',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: location.pathname === item.path ? '#F0F4F8' : '#F8F9FA',
                transform: 'translateX(4px)',
              },
            }}
          >
            <ListItemIcon
              sx={{
                color: location.pathname === item.path ? '#5B7C99' : '#6C757D',
                minWidth: 40,
                transition: 'color 0.2s ease-in-out',
              }}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.text}
              sx={{
                '& .MuiListItemText-primary': {
                  fontWeight: location.pathname === item.path ? 500 : 400,
                  fontSize: '0.875rem',
                  color: location.pathname === item.path ? '#5B7C99' : '#6C757D',
                  transition: 'all 0.2s ease-in-out',
                },
              }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          backgroundColor: isMobile ? 'linear-gradient(135deg, #4A90E2, #357ABD)' : '#FFFFFF',
          color: isMobile ? 'white' : '#2C3E50',
          boxShadow: isMobile ? '0 4px 20px rgba(74, 144, 226, 0.3)' : '0 2px 4px rgba(0, 0, 0, 0.08)',
          borderBottom: isMobile ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #E9ECEF',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          height: isMobile ? '56px' : '64px',
          minHeight: isMobile ? '56px' : '64px',
        }}
      >
        <Toolbar sx={{ minHeight: isMobile ? '56px' : '64px' }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ 
              mr: 2, 
              display: { md: 'none' },
              ...(isMobile && {
                padding: '8px'
              })
            }}
          >
            <MenuIcon />
          </IconButton>
          <Typography 
            variant={isMobile ? "subtitle1" : "h6"} 
            noWrap 
            component="div" 
            sx={{ 
              fontWeight: 600, 
              flexGrow: 1,
              ...(isMobile && {
                fontSize: '16px',
                fontWeight: 700
              })
            }}
          >
            {isMobile 
              ? (menuItems.find(item => item.path === location.pathname)?.text || 'Педиатрия')
              : (menuItems.find(item => item.path === location.pathname)?.text || 'Педиатрия')
            }
          </Typography>
          
          {/* Пользовательские функции */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: isMobile ? 0.5 : 1 
          }}>
            <Tooltip title="Поиск по разделам">
              <IconButton
                color="inherit"
                onClick={handleSearchClick}
                sx={isMobile ? { padding: '8px' } : {}}
              >
                <SearchIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Системные настройки">
              <IconButton
                color="inherit"
                onClick={() => handleNavigation('/system-settings')}
                sx={isMobile ? { padding: '8px' } : {}}
              >
                <SettingsIcon />
              </IconButton>
            </Tooltip>

            {isAuthenticated && !isMobile && (
              <>
                <Tooltip title="История действий">
                  <IconButton
                    color="inherit"
                    onClick={() => setHistoryPanelOpen(true)}
                  >
                    <Badge badgeContent={0} color="secondary">
                      <HistoryIcon />
                    </Badge>
                  </IconButton>
                </Tooltip>
                
                <Tooltip title="Избранное">
                  <IconButton
                    color="inherit"
                    onClick={() => setFavoritesPanelOpen(true)}
                  >
                    <Badge badgeContent={0} color="secondary">
                      <StarIcon />
                    </Badge>
                  </IconButton>
                </Tooltip>
              </>
            )}
            
            <Tooltip title={isAuthenticated ? "Профиль пользователя" : "Войти в систему"}>
              <IconButton
                color="inherit"
                onClick={handleUserMenuOpen}
                sx={isMobile ? { padding: '4px' } : {}}
              >
                {isAuthenticated ? (
                  <Avatar sx={{ 
                    width: isMobile ? 28 : 32, 
                    height: isMobile ? 28 : 32, 
                    bgcolor: isMobile ? 'rgba(255,255,255,0.2)' : 'secondary.main' 
                  }}>
                    {user?.name?.charAt(0).toUpperCase()}
                  </Avatar>
                ) : (
                  <PersonIcon />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              border: 'none',
              boxShadow: '2px 0 4px rgba(0, 0, 0, 0.08)',
              borderRight: '1px solid #E9ECEF',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              border: 'none',
              boxShadow: '2px 0 4px rgba(0, 0, 0, 0.08)',
              borderRight: '1px solid #E9ECEF',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        className="main-content"
        sx={{
          flexGrow: 1,
          p: isMobile ? 1 : { xs: 2, md: 3 },
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: isMobile ? '56px' : '64px',
          minHeight: isMobile ? 'calc(100vh - 56px)' : 'calc(100vh - 64px)',
          backgroundColor: isMobile ? '#f8f9fa' : '#FAFBFC',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          ...(isMobile && {
            paddingBottom: '100px', // Отступ для мобильной навигации
          })
        }}
      >
        {/* Хлебные крошки */}
        {breadcrumbs.length > 1 && !isMobile && (
          <Breadcrumbs
            items={breadcrumbs}
            onNavigate={handleNavigation}
            showSearch={true}
            onSearchClick={handleSearchClick}
            showHistory={isAuthenticated}
            onHistoryClick={handleHistoryClick}
          />
        )}

        {/* Основной контент */}
        {children}

        {/* Быстрые ссылки */}
        {currentSection !== '/' && !isMobile && (
          <QuickLinks
            currentSection={currentSection}
            onNavigate={handleNavigation}
            maxLinks={6}
            showCategory={true}
          />
        )}
      </Box>

      {/* Меню пользователя */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
          }
        }}
      >
        {isAuthenticated ? (
          <>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {user?.name}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {user?.role === 'doctor' ? 'Врач' : 'Медсестра'}
              </Typography>
              {user?.specialization && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {user.specialization}
                </Typography>
              )}
            </Box>
            <MenuItem onClick={() => setHistoryPanelOpen(true)}>
              <ListItemIcon>
                <HistoryIcon />
              </ListItemIcon>
              История действий
            </MenuItem>
            <MenuItem onClick={() => setFavoritesPanelOpen(true)}>
              <ListItemIcon>
                <StarIcon />
              </ListItemIcon>
              Избранное
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon />
              </ListItemIcon>
              Выйти
            </MenuItem>
          </>
        ) : (
          <MenuItem onClick={handleLogin}>
            <ListItemIcon>
              <LoginIcon />
            </ListItemIcon>
            Войти в систему
          </MenuItem>
        )}
      </Menu>

      {/* Модальное окно аутентификации */}
      <AuthModal
        open={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        onSuccess={handleAuthSuccess}
      />

      {/* Панель истории */}
      {historyPanelOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            right: 0,
            width: { xs: '100%', md: '400px' },
            height: '100vh',
            zIndex: 1300,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2
          }}
        >
          <Box sx={{ width: '100%', maxWidth: 400, maxHeight: '90vh' }}>
            <HistoryPanel
              userId={user?.id || 'guest'}
              onClose={() => setHistoryPanelOpen(false)}
            />
          </Box>
        </Box>
      )}

      {/* Панель избранного */}
      {favoritesPanelOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            right: 0,
            width: { xs: '100%', md: '400px' },
            height: '100vh',
            zIndex: 1300,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2
          }}
        >
          <Box sx={{ width: '100%', maxWidth: 400, maxHeight: '90vh' }}>
            <FavoritesPanel
              userId={user?.id || 'guest'}
              onClose={() => setFavoritesPanelOpen(false)}
            />
          </Box>
        </Box>
      )}

      {/* Поиск по разделам */}
      <SectionSearch
        open={showSearch}
        onClose={() => setShowSearch(false)}
        onNavigate={handleNavigation}
      />

      {/* Мобильная навигация */}
      {isMobileDevice && <MobileNavigation />}
    </Box>
  );
};

export default Layout;
